import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Generate a persistent visitor ID for marketing site
function getVisitorId() {
  let vid = localStorage.getItem('lodgiflow_visitor_id');
  if (!vid) {
    vid = 'v_' + Math.random().toString(36).substr(2, 12) + Date.now().toString(36);
    localStorage.setItem('lodgiflow_visitor_id', vid);
  }
  return vid;
}

function getDeviceType() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getSessionId() {
  let sid = sessionStorage.getItem('lodgiflow_session_id');
  if (!sid) {
    sid = 's_' + Math.random().toString(36).substr(2, 8) + Date.now().toString(36);
    sessionStorage.setItem('lodgiflow_session_id', sid);
  }
  return sid;
}

// ══════════════════════════════════════════════
// APP TRACKING HOOK (for authenticated users)
// ══════════════════════════════════════════════

export function useAnalytics(token, activeTab) {
  const lastTab = useRef('');
  const sessionId = useRef(getSessionId());
  const buffer = useRef([]);
  const flushTimer = useRef(null);

  const flush = useCallback(() => {
    if (buffer.current.length === 0 || !token) return;
    const events = [...buffer.current];
    buffer.current = [];
    axios.post(`${BACKEND_URL}/api/analytics/track-batch`, { events }, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [token]);

  const track = useCallback((eventType, page, feature = '', detail = '') => {
    buffer.current.push({
      event_type: eventType,
      page,
      feature,
      detail,
      session_id: sessionId.current,
      device_type: getDeviceType(),
      timestamp: new Date().toISOString(),
    });
    // Flush after 5 seconds or 10 events
    if (buffer.current.length >= 10) {
      flush();
    } else {
      clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(flush, 5000);
    }
  }, [flush]);

  // Track page views on tab change
  useEffect(() => {
    if (activeTab && activeTab !== lastTab.current) {
      lastTab.current = activeTab;
      track('page_view', activeTab);
    }
  }, [activeTab, track]);

  // Track session start
  useEffect(() => {
    if (token) {
      track('session_start', 'app');
    }
    return () => {
      flush(); // Flush remaining events on unmount
    };
  }, [token]); // eslint-disable-line

  // Flush on page unload
  useEffect(() => {
    const onUnload = () => flush();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [flush]);

  return { trackFeature: (feature, detail) => track('feature_click', lastTab.current, feature, detail) };
}


// ══════════════════════════════════════════════
// MARKETING SITE TRACKING (public, no auth)
// ══════════════════════════════════════════════

export function usePublicAnalytics() {
  const visitorId = useRef(getVisitorId());
  const lastPage = useRef('');

  const trackPublic = useCallback((page, section = '', action = '') => {
    // Parse UTM params
    const params = new URLSearchParams(window.location.search);
    axios.post(`${BACKEND_URL}/api/analytics/public`, {
      event_type: action ? 'action' : 'page_view',
      page,
      section,
      action,
      referrer: document.referrer || '',
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      device_type: getDeviceType(),
      screen_width: window.innerWidth,
      visitor_id: visitorId.current,
    }).catch(() => {});
  }, []);

  const trackPageView = useCallback((page) => {
    if (page !== lastPage.current) {
      lastPage.current = page;
      trackPublic(page);
    }
  }, [trackPublic]);

  return { trackPageView, trackPublic };
}
